/**
 * System Metrics Dashboard
 * Shows reference data ingestion status and signal collection metrics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Storage as StorageIcon,
  Wifi as WifiIcon,
  Bluetooth as BluetoothIcon,
  CellTower as CellTowerIcon,
  SatelliteAlt as GnssIcon,
  GpsFixed as GpsIcon,
  Person as PersonIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { apiService } from '@signals/app-shell/services/api';

interface RefDataSource {
  source_name: string;
  last_refresh: string | null;
  row_count: number | null;
  status: string | null;
  error_msg: string | null;
  refresh_interval_hours: number | null;
}

interface TableMetrics {
  total_rows: number;
  unique_devices: number;
  earliest: string | null;
  latest: string | null;
  last_24h: number;
  last_1h: number;
  error?: string;
  total_rows_approximate?: boolean;
  unique_devices_approximate?: boolean;
}

interface DeviceMetrics {
  entity_id: string;
  course_id: string;
  phone_app_id: string;
  gps_points: number;
  wifi_obs: number;
  bt_obs: number;
  cell_obs: number;
  gnss_obs: number;
}

interface SignalMetrics {
  tables: Record<string, TableMetrics>;
  devices: DeviceMetrics[];
  reference_tables: Record<string, number>;
  metrics_methodology?: string;
  device_breakdown_scope_days?: number;
  device_breakdown_limit?: number;
}

const SOURCE_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  oui: { label: 'IEEE OUI (MAC Manufacturers)', icon: <StorageIcon fontSize="small" /> },
  mcc_mnc: { label: 'MCC/MNC (Mobile Carriers)', icon: <CellTowerIcon fontSize="small" /> },
  ble_sig: { label: 'Bluetooth SIG (BLE Companies)', icon: <BluetoothIcon fontSize="small" /> },
  opencellid: { label: 'OpenCelliD (Cell Towers)', icon: <CellTowerIcon fontSize="small" /> },
  wigle: { label: 'WiGLE (WiFi Networks)', icon: <WifiIcon fontSize="small" /> },
};

const TABLE_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  device_tracks: { label: 'GPS Positions', icon: <GpsIcon fontSize="small" /> },
  signal_wifi: { label: 'WiFi Observations', icon: <WifiIcon fontSize="small" /> },
  signal_bluetooth: { label: 'Bluetooth Observations', icon: <BluetoothIcon fontSize="small" /> },
  signal_cellular: { label: 'Cellular Observations', icon: <CellTowerIcon fontSize="small" /> },
  signal_gnss: { label: 'GNSS Measurements', icon: <GnssIcon fontSize="small" /> },
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return 'Never';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function StatusChip({ status }: { status: string | null }) {
  if (status === 'ok') return <Chip icon={<CheckCircle />} label="OK" size="small" color="success" variant="outlined" />;
  if (status === 'error') return <Chip icon={<ErrorIcon />} label="Error" size="small" color="error" variant="outlined" />;
  if (status === 'refreshing') return <Chip icon={<CircularProgress size={14} />} label="Refreshing" size="small" color="warning" variant="outlined" />;
  return <Chip icon={<PendingIcon />} label="Pending" size="small" variant="outlined" />;
}

type DfpSensitivityProfile = 'conservative' | 'standard' | 'sensitive';

type DfpCopPollStatus = 'open' | 'unacknowledged';

interface DFPDetectionSettings {
  enabled: boolean;
  sensitivity_profile: DfpSensitivityProfile;
  min_confidence: number;
  warning_threshold: number;
  critical_threshold: number;
  cop_poll_status: DfpCopPollStatus;
  alert_auto_dismiss_hours: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

type DfpExclusionType = 'mac' | 'ip' | 'device_id' | 'ssid' | 'name' | 'other';

interface DfpExcludedDevice {
  id: number;
  identifier: string;
  normalized_identifier: string;
  identifier_type: DfpExclusionType;
  label: string | null;
  reason: string | null;
  applies_to: string;
  enabled: boolean;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface DfpTrustedWifiProfile {
  id: number;
  ssid: string;
  normalized_ssid: string;
  bssid: string | null;
  normalized_bssid: string | null;
  label: string | null;
  latitude: number | null;
  longitude: number | null;
  radius_m: number;
  security: string | null;
  gateway_ip: string | null;
  reason: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const DFP_PRESETS: Record<DfpSensitivityProfile, Pick<DFPDetectionSettings, 'min_confidence' | 'warning_threshold' | 'critical_threshold'>> = {
  conservative: { min_confidence: 0.30, warning_threshold: 0.50, critical_threshold: 0.78 },
  standard: { min_confidence: 0.20, warning_threshold: 0.40, critical_threshold: 0.70 },
  sensitive: { min_confidence: 0.12, warning_threshold: 0.30, critical_threshold: 0.60 },
};

const DEFAULT_DFP_SETTINGS: DFPDetectionSettings = {
  enabled: true,
  sensitivity_profile: 'standard',
  min_confidence: 0.20,
  warning_threshold: 0.40,
  critical_threshold: 0.70,
  cop_poll_status: 'open',
  alert_auto_dismiss_hours: 8,
};

const PROFILE_LABELS: Record<DfpSensitivityProfile, string> = {
  conservative: 'Conservative',
  standard: 'Standard',
  sensitive: 'Sensitive',
};

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function DFPDetectionSettingsPanel() {
  const [open, setOpen] = useState(true);
  const [settings, setSettings] = useState<DFPDetectionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const resp = await apiService.client.get('/admin/system-settings/dfp-detection', { timeout: 30000 });
      const data = resp.data || {};
      setSettings({
        ...DEFAULT_DFP_SETTINGS,
        ...data,
        cop_poll_status: data.cop_poll_status === 'unacknowledged' ? 'unacknowledged' : 'open',
        alert_auto_dismiss_hours:
          typeof data.alert_auto_dismiss_hours === 'number'
            ? Math.max(0, Math.min(168, data.alert_auto_dismiss_hours))
            : DEFAULT_DFP_SETTINGS.alert_auto_dismiss_hours,
      });
    } catch (err: any) {
      if (!silent) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load DFP detection settings');
        setSettings(DEFAULT_DFP_SETTINGS);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = (updates: Partial<DFPDetectionSettings>) => {
    setSettings(prev => (prev ? { ...prev, ...updates } : prev));
    setSuccess(null);
  };

  const handleProfileChange = (profile: DfpSensitivityProfile) => {
    updateSettings({
      sensitivity_profile: profile,
      ...DFP_PRESETS[profile],
    });
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const {
        enabled,
        sensitivity_profile,
        min_confidence,
        warning_threshold,
        critical_threshold,
        cop_poll_status,
        alert_auto_dismiss_hours,
      } = settings;
      const resp = await apiService.client.put('/admin/system-settings/dfp-detection', {
        enabled,
        sensitivity_profile,
        min_confidence,
        warning_threshold,
        critical_threshold,
        cop_poll_status,
        alert_auto_dismiss_hours,
      }, { timeout: 30000 });
      const data = resp.data || {};
      setSettings({
        ...DEFAULT_DFP_SETTINGS,
        ...data,
        cop_poll_status: data.cop_poll_status === 'unacknowledged' ? 'unacknowledged' : 'open',
        alert_auto_dismiss_hours:
          typeof data.alert_auto_dismiss_hours === 'number'
            ? Math.max(0, Math.min(168, data.alert_auto_dismiss_hours))
            : DEFAULT_DFP_SETTINGS.alert_auto_dismiss_hours,
      });
      setSuccess('DFP detection settings saved. The detection engine will pick them up on its next tick.');
      void fetchSettings({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save DFP detection settings');
    } finally {
      setSaving(false);
    }
  };

  const invalidThresholds = settings
    ? !(settings.min_confidence < settings.warning_threshold && settings.warning_threshold < settings.critical_threshold)
    : false;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ pb: open ? 2 : '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <TuneIcon color="primary" />
            <Box>
              <Typography variant="h6">DFP Detection Settings</Typography>
              <Typography variant="body2" color="text.secondary">
                Engine on/off and severity thresholds for the anomaly-scoring detectors.
                Verdict-engine alerts (WiFi/BLE/cellular entities) are evidence-based and
                not affected by these sliders.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(value => !value)} aria-label={open ? 'Collapse DFP settings' : 'Expand DFP settings'}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={open}>
          {loading && !settings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              {settings && (
                <Stack spacing={2.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enabled}
                        onChange={(e) => updateSettings({ enabled: e.target.checked })}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Enable DFP detection engine</Typography>
                        <Typography variant="caption" color="text.secondary">
                          When disabled, the engine skips detection ticks and stops creating new alerts.
                        </Typography>
                      </Box>
                    }
                  />

                  <FormControl size="small" sx={{ maxWidth: 320 }}>
                    <InputLabel id="dfp-sensitivity-profile-label">Sensitivity profile</InputLabel>
                    <Select
                      labelId="dfp-sensitivity-profile-label"
                      label="Sensitivity profile"
                      value={settings.sensitivity_profile}
                      onChange={(e) => handleProfileChange(e.target.value as DfpSensitivityProfile)}
                    >
                      {(Object.keys(PROFILE_LABELS) as DfpSensitivityProfile[]).map(profile => (
                        <MenuItem key={profile} value={profile}>{PROFILE_LABELS[profile]}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Minimum alert confidence</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lower values create more low-confidence alerts.
                      </Typography>
                      <Slider
                        value={settings.min_confidence}
                        min={0.05}
                        max={0.50}
                        step={0.01}
                        onChange={(_, value) => updateSettings({ min_confidence: value as number })}
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatPercent}
                        marks={[
                          { value: 0.12, label: 'Sensitive' },
                          { value: 0.20, label: 'Std' },
                          { value: 0.30, label: 'Quiet' },
                        ]}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Warning threshold</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Alerts at or above this confidence show as warnings.
                      </Typography>
                      <Slider
                        value={settings.warning_threshold}
                        min={0.20}
                        max={0.80}
                        step={0.01}
                        onChange={(_, value) => updateSettings({ warning_threshold: value as number })}
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatPercent}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2">Critical threshold</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Alerts at or above this confidence escalate to critical.
                      </Typography>
                      <Slider
                        value={settings.critical_threshold}
                        min={0.40}
                        max={0.95}
                        step={0.01}
                        onChange={(_, value) => updateSettings({ critical_threshold: value as number })}
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatPercent}
                      />
                    </Grid>
                  </Grid>

                  {invalidThresholds && (
                    <Alert severity="warning">
                      Thresholds must be ordered as minimum alert confidence &lt; warning &lt; critical.
                    </Alert>
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="dfp-cop-poll-status-label">COP alert poll status</InputLabel>
                        <Select
                          labelId="dfp-cop-poll-status-label"
                          label="COP alert poll status"
                          value={settings.cop_poll_status}
                          onChange={(e) => updateSettings({
                            cop_poll_status: e.target.value as DfpCopPollStatus,
                          })}
                        >
                          <MenuItem value="open">Open only</MenuItem>
                          <MenuItem value="unacknowledged">Open + auto-resolved</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Controls which DFP alerts the COP map/timeline poll. Default is open only.
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Auto-dismiss open alerts (hours)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Open alerts older than this are acknowledged by the system. 0 disables.
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                          value={settings.alert_auto_dismiss_hours}
                          min={0}
                          max={168}
                          step={1}
                          onChange={(_, value) => updateSettings({
                            alert_auto_dismiss_hours: value as number,
                          })}
                          valueLabelDisplay="auto"
                          marks={[
                            { value: 0, label: 'Off' },
                            { value: 8, label: '8h' },
                            { value: 24, label: '24h' },
                            { value: 168, label: '7d' },
                          ]}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Hours"
                          value={settings.alert_auto_dismiss_hours}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            if (Number.isNaN(raw)) return;
                            updateSettings({
                              alert_auto_dismiss_hours: Math.max(0, Math.min(168, raw)),
                            });
                          }}
                          inputProps={{ min: 0, max: 168, step: 1 }}
                          sx={{ width: 96 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {settings.updated_at
                        ? `Last updated ${formatTimestamp(settings.updated_at)}${settings.updated_by ? ` by ${settings.updated_by}` : ''}`
                        : 'Using default DFP thresholds'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button onClick={() => handleProfileChange('standard')} disabled={saving}>
                        Reset Standard
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || invalidThresholds}
                      >
                        Save Settings
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

export function DFPKnownDeviceExclusionsPanel() {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState<DfpExcludedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    identifier: '',
    identifier_type: 'mac' as DfpExclusionType,
    applies_to: 'all',
    label: '',
    reason: '',
  });

  const fetchExclusions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.client.get('/admin/dfp/exclusions', { timeout: 30000 });
      setItems(resp.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load known device exclusions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExclusions();
  }, [fetchExclusions]);

  const handleAdd = async () => {
    if (!form.identifier.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.client.post('/admin/dfp/exclusions', {
        identifier: form.identifier.trim(),
        identifier_type: form.identifier_type,
        label: form.label.trim() || null,
        reason: form.reason.trim() || null,
        applies_to: form.applies_to,
        enabled: true,
      }, { timeout: 30000 });
      setForm(prev => ({ ...prev, identifier: '', label: '', reason: '' }));
      await fetchExclusions();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to add exclusion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.client.delete(`/admin/dfp/exclusions/${id}`, { timeout: 30000 });
      setItems(prev => prev.filter(item => item.id !== id));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to remove exclusion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ pb: open ? 2 : '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6">Known Device Exclusions</Typography>
            <Typography variant="body2" color="text.secondary">
              Exclude trusted MACs, IPs, SSIDs, phones, AirPods, and counterpart devices from selected DFP rules.
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(value => !value)} aria-label={open ? 'Collapse known device exclusions' : 'Expand known device exclusions'}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={open}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="dfp-exclusion-type-label">Type</InputLabel>
                  <Select
                    labelId="dfp-exclusion-type-label"
                    label="Type"
                    value={form.identifier_type}
                    onChange={(e) => setForm(prev => ({ ...prev, identifier_type: e.target.value as DfpExclusionType }))}
                  >
                    <MenuItem value="mac">MAC</MenuItem>
                    <MenuItem value="ip">IP</MenuItem>
                    <MenuItem value="device_id">Device ID</MenuItem>
                    <MenuItem value="ssid">SSID</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="dfp-exclusion-scope-label">Scope</InputLabel>
                  <Select
                    labelId="dfp-exclusion-scope-label"
                    label="Scope"
                    value={form.applies_to}
                    onChange={(e) => setForm(prev => ({ ...prev, applies_to: e.target.value }))}
                  >
                    <MenuItem value="all">All DFP</MenuItem>
                    <MenuItem value="wifi">WiFi rules</MenuItem>
                    <MenuItem value="cotraveler">Co-traveler</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Identifier"
                  placeholder="AA:BB:CC:DD:EE:FF"
                  value={form.identifier}
                  onChange={(e) => setForm(prev => ({ ...prev, identifier: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label"
                  placeholder="Joe AirPods"
                  value={form.label}
                  onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reason"
                  placeholder="Known friendly device"
                  value={form.reason}
                  onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
                  onClick={handleAdd}
                  disabled={saving || !form.identifier.trim()}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Identifier</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Scope</strong></TableCell>
                    <TableCell><strong>Label</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell><strong>Updated</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">
                          No known devices excluded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : items.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
                          {item.identifier}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.normalized_identifier}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.identifier_type.toUpperCase()}</TableCell>
                      <TableCell>{item.applies_to === 'all' ? 'All DFP' : item.applies_to}</TableCell>
                      <TableCell>{item.label || '—'}</TableCell>
                      <TableCell>{item.reason || '—'}</TableCell>
                      <TableCell>{formatTimestamp(item.updated_at || item.created_at || null)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove exclusion">
                          <span>
                            <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={saving}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export function DFPTrustedWifiProfilesPanel() {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState<DfpTrustedWifiProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    ssid: '',
    bssid: '',
    label: '',
    latitude: '',
    longitude: '',
    radius_m: '150',
    security: '',
    gateway_ip: '',
    reason: '',
  });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.client.get('/admin/dfp/trusted-wifi', { timeout: 30000 });
      setItems(resp.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load trusted WiFi profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleAdd = async () => {
    if (!form.ssid.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.client.post('/admin/dfp/trusted-wifi', {
        ssid: form.ssid.trim(),
        bssid: form.bssid.trim() || null,
        label: form.label.trim() || null,
        latitude: form.latitude.trim() ? Number(form.latitude) : null,
        longitude: form.longitude.trim() ? Number(form.longitude) : null,
        radius_m: Number(form.radius_m) || 150,
        security: form.security.trim() || null,
        gateway_ip: form.gateway_ip.trim() || null,
        reason: form.reason.trim() || null,
        enabled: true,
      }, { timeout: 30000 });
      setForm(prev => ({ ...prev, ssid: '', bssid: '', label: '', gateway_ip: '', reason: '' }));
      await fetchProfiles();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to add trusted WiFi profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.client.delete(`/admin/dfp/trusted-wifi/${id}`, { timeout: 30000 });
      setItems(prev => prev.filter(item => item.id !== id));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to remove trusted WiFi profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ pb: open ? 2 : '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6">Trusted WiFi Profiles</Typography>
            <Typography variant="body2" color="text.secondary">
              Location-bound SSID/BSSID fingerprints for trusted venues, corporate APs, and public networks.
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(value => !value)} aria-label={open ? 'Collapse trusted WiFi profiles' : 'Expand trusted WiFi profiles'}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={open}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              Profiles suppress expected same-location WiFi noise, but still alert if the same BSSID appears elsewhere or a trusted SSID appears outside its expected radius.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="SSID" value={form.ssid} onChange={(e) => setForm(prev => ({ ...prev, ssid: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="BSSID (optional)" value={form.bssid} onChange={(e) => setForm(prev => ({ ...prev, bssid: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField fullWidth size="small" label="Label" placeholder="Starbucks - Main St" value={form.label} onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))} />
              </Grid>
              <Grid item xs={6} md={1.4}>
                <TextField fullWidth size="small" label="Lat" value={form.latitude} onChange={(e) => setForm(prev => ({ ...prev, latitude: e.target.value }))} />
              </Grid>
              <Grid item xs={6} md={1.4}>
                <TextField fullWidth size="small" label="Lon" value={form.longitude} onChange={(e) => setForm(prev => ({ ...prev, longitude: e.target.value }))} />
              </Grid>
              <Grid item xs={6} md={1.2}>
                <TextField fullWidth size="small" label="Radius m" value={form.radius_m} onChange={(e) => setForm(prev => ({ ...prev, radius_m: e.target.value }))} />
              </Grid>
              <Grid item xs={6} md={1}>
                <TextField fullWidth size="small" label="Security" value={form.security} onChange={(e) => setForm(prev => ({ ...prev, security: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button fullWidth variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />} onClick={handleAdd} disabled={saving || !form.ssid.trim()}>
                  Add
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Reason" placeholder="Why this network is trusted" value={form.reason} onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))} />
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>SSID</strong></TableCell>
                    <TableCell><strong>BSSID</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Traits</strong></TableCell>
                    <TableCell><strong>Updated</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={22} /></TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No trusted WiFi profiles yet.</Typography></TableCell></TableRow>
                  ) : items.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{item.ssid}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.label || item.normalized_ssid}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: '"Roboto Mono", monospace' }}>{item.bssid || 'Any'}</TableCell>
                      <TableCell>
                        {item.latitude != null && item.longitude != null
                          ? `${item.latitude.toFixed(5)}, ${item.longitude.toFixed(5)} (${Math.round(item.radius_m)}m)`
                          : `Any location (${Math.round(item.radius_m)}m radius if set)`}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">Security: {item.security || 'Any'}</Typography>
                        <Typography variant="caption" display="block">Gateway: {item.gateway_ip || 'Any'}</Typography>
                        {item.reason && <Typography variant="caption" display="block">Reason: {item.reason}</Typography>}
                      </TableCell>
                      <TableCell>{formatTimestamp(item.updated_at || item.created_at || null)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove trusted profile">
                          <span>
                            <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={saving}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

const SystemMetrics: React.FC = () => {
  const [refData, setRefData] = useState<RefDataSource[]>([]);
  const [signalMetrics, setSignalMetrics] = useState<SignalMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operatorBreakdownOpen, setOperatorBreakdownOpen] = useState(false);
  const [operatorBreakdownLoading, setOperatorBreakdownLoading] = useState(false);
  const [operatorBreakdownLoaded, setOperatorBreakdownLoaded] = useState(false);
  const [operatorBreakdownError, setOperatorBreakdownError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOperatorBreakdownLoaded(false);
    setOperatorBreakdownError(null);
    const [refResult, metricsResult] = await Promise.allSettled([
      apiService.client.get('/admin/reference-data/status', { timeout: 30000 }),
      // Aggregates are much cheaper server-side now; keep a generous cap for cold caches.
      apiService.client.get('/admin/metrics/signal-collection?include_device_breakdown=false', { timeout: 120000 }),
    ]);

    const errors: string[] = [];
    if (refResult.status === 'fulfilled') {
      setRefData(refResult.value.data);
    } else {
      setRefData([]);
      errors.push(`Reference status: ${refResult.reason?.response?.data?.detail || refResult.reason?.message || 'failed to load'}`);
    }

    if (metricsResult.status === 'fulfilled') {
      setSignalMetrics(metricsResult.value.data);
    } else {
      setSignalMetrics(null);
      errors.push(`Signal metrics: ${metricsResult.reason?.response?.data?.detail || metricsResult.reason?.message || 'failed to load'}`);
    }

    if (errors.length > 0) setError(errors.join(' | '));
    setLoading(false);
  }, []);

  const loadOperatorBreakdown = useCallback(async () => {
    if (operatorBreakdownLoading || operatorBreakdownLoaded) return;
    setOperatorBreakdownLoading(true);
    setOperatorBreakdownError(null);
    try {
      const resp = await apiService.client.get('/admin/metrics/signal-collection?include_device_breakdown=true', { timeout: 120000 });
      setSignalMetrics(prev => ({
        ...(prev || resp.data),
        devices: resp.data.devices || [],
        device_breakdown_scope_days: resp.data.device_breakdown_scope_days,
        device_breakdown_limit: resp.data.device_breakdown_limit,
      }));
      setOperatorBreakdownLoaded(true);
    } catch (err: any) {
      setOperatorBreakdownError(err?.response?.data?.detail || err?.message || 'Failed to load per-operator collection');
    } finally {
      setOperatorBreakdownLoading(false);
    }
  }, [operatorBreakdownLoaded, operatorBreakdownLoading]);

  const handleOperatorBreakdownToggle = useCallback(() => {
    setOperatorBreakdownOpen(prev => {
      const next = !prev;
      if (next && !operatorBreakdownLoaded && !operatorBreakdownLoading) {
        void loadOperatorBreakdown();
      }
      return next;
    });
  }, [loadOperatorBreakdown, operatorBreakdownLoaded, operatorBreakdownLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">System Metrics</Typography>
        <Tooltip title="Refresh metrics">
          <span>
            <IconButton onClick={fetchData} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DFPDetectionSettingsPanel />
      <Alert severity="info" sx={{ mb: 3 }}>
        Device exclusions, trusted WiFi profiles, watchlist, and detector health are managed
        on the DFP page (Trusted and Detectors tabs).
      </Alert>

      {loading && !signalMetrics && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Signal Collection Summary */}
      {signalMetrics && (
        <>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Signal Collection</Typography>
          {signalMetrics.metrics_methodology && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {signalMetrics.metrics_methodology}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(signalMetrics.tables).map(([table, data]) => {
          const info = TABLE_LABELS[table] || { label: table, icon: <StorageIcon fontSize="small" /> };
          const m = data as TableMetrics;
          return (
            <Grid item xs={12} sm={6} md={4} key={table}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {info.icon}
                    <Typography variant="subtitle2">{info.label}</Typography>
                  </Box>
                  {m.error ? (
                    <Typography variant="caption" color="error">{m.error}</Typography>
                  ) : (
                    <>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {m.total_rows_approximate ? `~${formatNumber(m.total_rows)}` : formatNumber(m.total_rows)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {m.unique_devices_approximate ? `~${formatNumber(m.unique_devices)}` : formatNumber(m.unique_devices)} devices
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last 24h: {formatNumber(m.last_24h)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last 1h: {formatNumber(m.last_1h)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Range: {m.earliest ? new Date(m.earliest).toLocaleDateString() : '—'} to {m.latest ? formatTimestamp(m.latest) : '—'}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
          </Grid>
        </>
      )}

      {/* Per-Operator Breakdown - lazy loaded because this is the heaviest metrics query */}
      {signalMetrics && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ pb: operatorBreakdownOpen ? 2 : '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="h6">
                  Per-Operator Collection
                  {operatorBreakdownLoaded && signalMetrics.device_breakdown_scope_days != null && signalMetrics.device_breakdown_limit != null && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      (last {signalMetrics.device_breakdown_scope_days} days, top {signalMetrics.device_breakdown_limit} by GPS)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Collapsed by default. Opens on demand to avoid the heavier per-operator aggregate during normal page load.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOperatorBreakdownToggle}
                endIcon={operatorBreakdownOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                disabled={operatorBreakdownLoading}
              >
                {operatorBreakdownLoading ? 'Loading...' : operatorBreakdownOpen ? 'Hide' : 'Show'}
              </Button>
            </Box>
            <Collapse in={operatorBreakdownOpen}>
              <Box sx={{ mt: 2 }}>
                {operatorBreakdownError && <Alert severity="error" sx={{ mb: 2 }}>{operatorBreakdownError}</Alert>}
                {operatorBreakdownLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : signalMetrics.devices.length === 0 ? (
                  <Alert severity="info">No per-operator collection rows found for the current window.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Operator</strong></TableCell>
                          <TableCell><strong>Mission</strong></TableCell>
                          <TableCell align="right"><strong>GPS</strong></TableCell>
                          <TableCell align="right"><strong>WiFi</strong></TableCell>
                          <TableCell align="right"><strong>BLE</strong></TableCell>
                          <TableCell align="right"><strong>Cellular</strong></TableCell>
                          <TableCell align="right"><strong>GNSS</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {signalMetrics.devices.map((d) => {
                          const total = d.gps_points + d.wifi_obs + d.bt_obs + d.cell_obs + d.gnss_obs;
                          return (
                            <TableRow key={`${d.entity_id}-${d.phone_app_id}`}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                  {d.entity_id}
                                </Box>
                              </TableCell>
                              <TableCell>{d.course_id}</TableCell>
                              <TableCell align="right">{formatNumber(d.gps_points)}</TableCell>
                              <TableCell align="right">{formatNumber(d.wifi_obs)}</TableCell>
                              <TableCell align="right">{formatNumber(d.bt_obs)}</TableCell>
                              <TableCell align="right">{formatNumber(d.cell_obs)}</TableCell>
                              <TableCell align="right">{formatNumber(d.gnss_obs)}</TableCell>
                              <TableCell align="right"><strong>{formatNumber(total)}</strong></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Reference Data Sources */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>Reference Data (VM Ingestion)</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Rows</strong></TableCell>
              <TableCell><strong>Last Refresh</strong></TableCell>
              <TableCell><strong>Interval</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {refData.map((r) => {
              const info = SOURCE_LABELS[r.source_name] || { label: r.source_name, icon: <StorageIcon fontSize="small" /> };
              return (
                <TableRow key={r.source_name}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {info.icon}
                      {info.label}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.status} />
                    {r.error_msg && (
                      <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5, maxWidth: 200 }}>
                        {r.error_msg.substring(0, 80)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{r.row_count ? formatNumber(r.row_count) : '—'}</TableCell>
                  <TableCell>{formatTimestamp(r.last_refresh)}</TableCell>
                  <TableCell>{r.refresh_interval_hours ? `${r.refresh_interval_hours}h` : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reference Table Counts */}
      {signalMetrics && signalMetrics.reference_tables && (
        <>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Reference Table Sizes</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Approximate live tuple counts from pg_stat (same as telemetry cards).
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(signalMetrics.reference_tables).map(([table, count]) => (
              <Grid item xs={6} sm={4} md={2.4} key={table}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                    <Typography variant="h5" fontWeight="bold">~{formatNumber(count as number)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {table.replace('ref_', '').replace('_', ' ')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SystemMetrics;
